import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from '../misc/Loading'
import { getModule, updateModule, resetUpdateModule, resetGetModule, ModuleState } from '../../store/ModuleAction';
import StatusCode from '../../util/StatusCode';
import BaseConfiguration from './components/BaseConfiguration';
import { CoreModuleView, ModuleType } from '../../models/CoreModels';
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next';
import { EmptyRequestState, RequestState } from '../../models/RequestState';
import { MeState } from '../../store/MeAction';
import SchoolUnitsConfiguration from './components/SchoolUnitsConfiguration';
import ModuleConfiguration from './ModuleConfiguration';
import { isEqual } from 'lodash';
import { RootState } from "../../store/store";
import { FlowSyncAccessPolicy, isAuthorized } from '../../models/FlowSyncAccessPolicy';


interface ModuleViewState {
    module: ModuleState
    me: MeState
};

const ModuleView = () => {
    const dispatch = useDispatch();
    const { moduleId } = useParams();
    const module = useSelector<ModuleViewState, RequestState<CoreModuleView>>(state => state.module.module);
    const updatedModule = useSelector<ModuleViewState, EmptyRequestState>(state => state.module.updatedModule);    
    const [mergedModule, setMergedModule] = useState<CoreModuleView>();
    const [invalidConfiguration, setInvalidConfiguration] = useState(false);
    const [invalidBaseConfiguration, setInvalidBaseConfiguration] = useState(false);
    const [invalidSchoolUnitsConfiguration, setInvalidSchoolUnitsConfiguration] = useState(false);
    const [warnings, setWarnings] = useState<({ configuration: string[], schoolUnits: string[] })>({ configuration: [], schoolUnits: [] });
    const [additionalAppTokenScopes, setAdditionalAppTokenScopes] = useState<string[]>([]);

    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const editModuleAccess = useMemo(() => {
        return isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_MODULE);
    }, [authorizedAccessPolicies])

    const { t } = useTranslation(['configuration', 'common']);


    useEffect(() => {
        if (moduleId) {
            getModule(moduleId)(dispatch)
            return () => {
                resetUpdateModule()(dispatch);
                resetGetModule()(dispatch);
            }
        }
    }, [dispatch, moduleId]);

    useEffect(() => {
        if (module.code === StatusCode.COMPLETE) {
            setMergedModule(module.data);
        }
    }, [module]);

    const saveUpdates = async () => {
        if (mergedModule && moduleId) {
            const res = await updateModule(mergedModule)(dispatch);
            await getModule(moduleId)(dispatch);
            if (res?.status === 200) {
                setWarnings({ configuration: [], schoolUnits: [] })
            }
        }
    }

    const isActive = () => {
        if (module.code === StatusCode.COMPLETE && mergedModule) {
            return mergedModule.active;
        };
        if (module.code === StatusCode.COMPLETE) {
            return module.data.active;
        }
        return false;
    };

    const selectSchoolUnitsList = (module: CoreModuleView) => {
        switch (module.moduleType) {
            case ModuleType.ImportModule: return module.importSchoolUnits;
            case ModuleType.ExportModule: return module.exportSchoolUnits;
            default: return [];
        }
    }

    const renderSaveStatus = () => {
        switch (updatedModule.code) {
            case StatusCode.PENDING: return <Loading />;
            case StatusCode.ERROR: return <>
                <FontAwesomeIcon icon="exclamation-triangle" style={{ fontSize: '22px', verticalAlign: 'middle', color: '#ffc107' }} />
                <span style={{ marginLeft: '0.5rem' }}>{t('error.somethingWentWrong', { ns: 'common' })}</span>
            </>;
            case StatusCode.COMPLETE: return <>
                <FontAwesomeIcon icon="check" style={{ fontSize: '22px', verticalAlign: 'middle', color: '#27807d' }} />
                <span style={{ marginLeft: '0.5rem' }}>{t('moduleView.saved')}</span>
            </>;
            default: return null;
        }
    }

    const renderWarnings = () => {
        const renderWarnings = Object.values(warnings);

        if (renderWarnings.some(category => Object.keys(category).length > 0)) {
            return <div style={{ color: 'red' }}>
                <p>{t('warning', { ns: 'common' })}!</p>
                {renderWarnings.map(category => category.map((message, index) => <p key={index}>{message}.</p>))}
            </div>
        }

        return null;
    }

    if (module.code === StatusCode.PENDING || mergedModule === undefined) {
        return <Loading />;
    } else if (module.code === StatusCode.ERROR) {
        return module.error.message;
    } else if (module.code === StatusCode.NONE) {
        return null;
    }

    const isInvalid = invalidBaseConfiguration || invalidConfiguration || invalidSchoolUnitsConfiguration;
    const hasUpdates = !isEqual(module.data, mergedModule);
    const disableSave = isInvalid || (!hasUpdates) || (!editModuleAccess);

    return <>
        <BaseConfiguration
            originalModule={module.data}
            disabled={!editModuleAccess}
            updatedModule={mergedModule}
            setUpdateModule={merge => setMergedModule(merge)}
            moduleId={moduleId}
            updateValidation={(value) => setInvalidBaseConfiguration(value)}
            additionalAppTokenScopes={additionalAppTokenScopes}
        />

        <ModuleConfiguration
            originalConfiguration={module.data.configuration}
            disabled={!editModuleAccess}
            active={isActive()}
            moduleTypeName={module.data.moduleTypeName}
            setUpdateConfiguration={merge => setMergedModule({ ...mergedModule, configuration: merge })}
            updatedConfiguration={mergedModule.configuration}
            updateValidation={(value) => setInvalidConfiguration(value)}
            updateWarnings={value => setWarnings({ ...warnings, configuration: value })}
            exportSchoolUnits={module.data.exportSchoolUnits}
            moduleId={moduleId}
            setAdditionalAppTokenScopes={scopes => setAdditionalAppTokenScopes(scopes)}
        />

        <SchoolUnitsConfiguration
            schoolUnits={selectSchoolUnitsList(module.data)}
            moduleType={module.data.moduleType}
            mergeUpdate={(name, updated) => setMergedModule({ ...mergedModule, [name]: updated })}
            disabled={!editModuleAccess}
            selectSchoolUnits={module.data.selectSchoolUnits}
            mergedSchoolUnits={selectSchoolUnitsList(mergedModule)}
            warnings={value => setWarnings({ ...warnings, schoolUnits: value })}
            updateValidation={value => setInvalidSchoolUnitsConfiguration(value)}
        />

        <div className="edit_module">
            {isActive() && renderWarnings()}
            <Button color="primary" disabled={disableSave} onClick={saveUpdates}>
                {t('action.save', { ns: 'common' })}
            </Button>
            <div style={{ marginLeft: '1rem', display: 'inline-block' }}>
                {renderSaveStatus()}
            </div>
        </div>
    </>;

}

export default ModuleView;
